import React, { useEffect, useState } from 'react';
import client from '../api/client';
import PeriodFilter, { calcRange } from '../components/PeriodFilter';
import PaginationFooter from '../components/PaginationFooter';

const THX_RED = '#B71530';

const STATUS_BADGES = {
  completed: 'bg-success',
  failed: 'bg-danger',
  cancelled: 'bg-warning text-dark',
  running: 'bg-primary',
};

function fmtDatetime(s) {
  if (!s) return '–';
  try { return new Date(s).toLocaleString('en-GB'); } catch { return s; }
}

function duration(start, end) {
  if (!start || !end) return '–';
  try {
    const diff = (new Date(end) - new Date(start)) / 1000;
    if (diff < 60) return `${Math.round(diff)}s`;
    return `${Math.round(diff / 60)}m ${Math.round(diff % 60)}s`;
  } catch {
    return '–';
  }
}


export default function ProcessingRuns() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [preset, setPreset] = useState('30');
  const [dateFrom, setDateFrom] = useState(() => calcRange('30').from);
  const [dateTo,   setDateTo]   = useState(() => calcRange('30').to);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;
    client.get('/processing-runs/', { params })
      .then(r => setRuns(r.data.runs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const handlePageSizeChange = (e) => { setPageSize(Number(e.target.value)); setPage(1); };

  const totalPages = Math.max(1, Math.ceil(runs.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = runs.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const latest = runs[0];

  return (
    <div>
      {/* Latest run summary */}
      {latest && (
        <div className={`alert ${latest.status === 'completed' ? 'alert-success' : latest.status === 'failed' ? 'alert-danger' : 'alert-info'} mb-4`} style={{ fontSize: '14px' }}>
          <strong>Latest Run:</strong>{' '}
          {fmtDatetime(latest.started_at)} — Status:{' '}
          <span className={`badge ${STATUS_BADGES[latest.status] || 'bg-secondary'}`}>{latest.status}</span>
          {' '}| Emails: {latest.emails_processed} | Invoices: {latest.invoices_extracted} | Exceptions: {latest.exceptions_raised}
          {latest.error_message && (
            <div className="mt-1" style={{ fontSize: '12px' }}>Error: {latest.error_message}</div>
          )}
        </div>
      )}

      <PeriodFilter
        preset={preset} dateFrom={dateFrom} dateTo={dateTo}
        onPresetChange={d => { const r = calcRange(d); setPreset(d); setDateFrom(r.from); setDateTo(r.to); setPage(1); }}
        onFromChange={e => { setDateFrom(e.target.value); setPreset('custom'); setPage(1); }}
        onToChange={e => { setDateTo(e.target.value); setPreset('custom'); setPage(1); }}
      />

      <div className="card">
        <div className="card-header py-2">
          <span style={{ fontSize: '14px', fontWeight: '600' }}>Processing History ({runs.length})</span>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-danger spinner-border-sm" /></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Started</th>
                    <th>Completed</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th className="text-end">Emails</th>
                    <th className="text-end">Invoices</th>
                    <th className="text-end">Exceptions</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 && (
                    <tr><td colSpan="9" className="text-center text-muted py-4">No processing runs yet</td></tr>
                  )}
                  {paginated.map(run => (
                    <tr key={run.id}>
                      <td>{run.id}</td>
                      <td>{fmtDatetime(run.started_at)}</td>
                      <td>{fmtDatetime(run.completed_at)}</td>
                      <td>{duration(run.started_at, run.completed_at)}</td>
                      <td>
                        <span className={`badge ${STATUS_BADGES[run.status] || 'bg-secondary'}`}>
                          {run.status}
                        </span>
                      </td>
                      <td className="text-end">{run.emails_processed}</td>
                      <td className="text-end">{run.invoices_extracted}</td>
                      <td className="text-end">{run.exceptions_raised}</td>
                      <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '11px', color: '#dc3545' }}>
                        {run.error_message || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && runs.length > 0 && (
          <PaginationFooter
            page={currentPage}
            totalPages={totalPages}
            total={runs.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </div>
  );
}
